import { site } from '../content/site';
import './About.css';
import './Capabilities.css';

/**
 * About page content. Three blocks:
 *
 *  1. Intro — eyebrow, heading, conversational bio + a portrait placeholder.
 *  2. Gallery — two further placeholder images for action / lifestyle shots.
 *  3. Beyond work — the hobbies grid driven by `site.hobbies`.
 *
 * Placeholders render a labelled striped block until you drop real images
 * into `public/about/` and add a `src`. See <AboutImage /> at the bottom.
 */
export default function About() {
  return (
    <>
      {/* Intro */}
      <section
        className="about__intro section"
        aria-labelledby="about-heading"
      >
        <div className="container about__intro-grid">
          <div className="about__intro-text">
            <span className="eyebrow">About</span>
            <h1 id="about-heading" className="about__heading display">
              Hello, I’m Matt.
            </h1>
            <div className="about__body">
              <p>
                As a Product and UX Designer with a background in creative, art direction, and advertising, I apply broad thinking to digital product design, balancing strategy, usability, accessibility, and visual craft. My multidisciplinary background shapes how I approach problems. Consideration of the full experience, from brand expression through to how people actually use a product day to day.
              </p>
              <p>
                My current work focuses on e-commerce, design systems, accessibility, and data-driven UX. I enjoy simplifying complexity, testing ideas early, and using a combination of design and AI tools to build products that are intuitive. My approach combines creative thinking with evidence-led decision making, helping teams deliver experiences that are both effective and engaging.
              </p>
              
            </div>
          </div>

          {/* Portrait — first of three placeholders */}
          <AboutImage
            src="/about/santorini.jpeg"
            label="Portrait"
            alt="A portrait photo of Matt and his wife in Santorini, Greece."
            aspectRatio="4 / 5"
          />
        </div>
      </section>

      {/* Beyond work — strapline, gallery, then hobbies grid */}
      <section
        id="beyond-work"
        className="capabilities section about__beyond-work"
        aria-labelledby="hobbies-heading"
      >
        <div className="container">
          <div className="capabilities__head">
            <span className="eyebrow">Beyond work</span>
            <h2 id="hobbies-heading" className="capabilities__heading display">
              I’m just as passionate about what I do outside of work.
            </h2>
            <div className="about__body">
              <p>
                I live in Macclesfield, Cheshire with my wife and two boys. Outside of work I'm either at the gym or in the Peak District, mountain biking or bouldering, chasing that adrenaline rush!
              </p>
            </div>
          </div>

          {/* Two images sit between the strapline and the hobbies grid */}
          <div className="about__gallery-grid">
            <AboutImage
              src="/about/mtb3.jpeg"
              label="On the trails"
              alt="Mountain biking racing."
              aspectRatio="3 / 2"
            />
            <AboutImage
              src="/about/bouldering_peaks.jpg"
              label="At the wall"
              alt="Bouldering in the Peak District"
              aspectRatio="3 / 2"
            />
            
          </div>

          
        </div>
      </section>
    </>
  );
}

/**
 * A single about-page image. Renders the `src` if provided, otherwise a
 * labelled striped placeholder block — so the layout reads complete before
 * real photography is added.
 */
function AboutImage({
  src,
  label,
  alt,
  aspectRatio,
}: {
  src?: string;
  label: string;
  alt: string;
  aspectRatio?: string;
}) {
  if (src) {
    return (
      <figure className="about__figure">
        <img src={src} alt={alt} loading="lazy" style={{ aspectRatio }} />
      </figure>
    );
  }
  return (
    <figure
      className="about__figure about__figure--placeholder"
      aria-label={alt}
    >
      <div className="about__placeholder" style={{ aspectRatio }}>
        <span className="about__placeholder-kind">Image placeholder</span>
        <span className="about__placeholder-label">{label}</span>
      </div>
    </figure>
  );
}
